import type { ModelRole, RetentionMode } from "../models.ts";
import type { OfferingKey } from "./offerings.ts";

/**
 * Labs, families and versions (openspec change `model-picking`, D3, amended by Q18b): the
 * catalogue groups each offering by the lab that makes it and the family it belongs to, and
 * orders a family's versions. Labs, prefixes, families and default tiers are generated by
 * `catalogue:sync` (`labs.generated.ts`); `family-overrides.ts` holds the curated exceptions. A
 * family's versions come from the offerings and the canonical layer (`family`, `releaseDate`,
 * route `status`). Resolution never names a lab or a family: it reads tiers (the effort map,
 * neighbours), so a new family needs no code.
 */

import { FAMILY_OVERRIDES, INSTANCE_DEFAULT_LABS } from "./family-overrides.ts";
import { GENERATED_FAMILIES, GENERATED_LABS } from "./labs.generated.ts";
import { type EffortTier, FAMILY_TIERS, type FamilyTier } from "./tiers.ts";

export {
  FAMILY_OVERRIDES,
  INSTANCE_DEFAULT_LABS,
  LAB_OVERRIDES,
  LAB_REASONING,
  type LabReasoningStyle,
} from "./family-overrides.ts";
export { type EffortTier, FAMILY_TIERS, type FamilyTier } from "./tiers.ts";
export { compareParts, parsedVersion } from "./version-parse.ts";

export interface ModelFamily {
  /** `<lab>/<canonical family>`, e.g. `anthropic/claude-sonnet`. */
  readonly id: string;
  /** The canonical `lab`. */
  readonly lab: string;
  /** Derived by the sync or pinned in `FAMILY_OVERRIDES`, never renamed by a snapshot alone. */
  readonly displayName: string;
  readonly tier: FamilyTier;
  /**
   * The family's rules (model-picking D3, slice 4b), applied to every version the catalogue sync
   * generates: the Bedrock retention mode its models require, and the version of its
   * implications (a consent is to one version of them; bump it when the rules change).
   */
  readonly retention: RetentionMode;
  readonly implicationsVersion: number;
  /**
   * For a lab whose reasoning style is `anthropic`: the first version that takes adaptive
   * reasoning (an effort level), e.g. `[4, 6]`; older versions take a thinking budget. Adaptive
   * is the opt-in (D15, coordinator 2026-11-02): a newly catalogued older model must never be
   * sent a setting it refuses.
   */
  readonly adaptiveReasoningFrom: readonly number[] | null;
}

/**
 * The served labs (model-picking D3 amended by Q18b), generated by `catalogue:sync` from the
 * pinned snapshot: display names (product names, not translated) and Bedrock model ID prefixes
 * (`<prefix>.<model>`, `<geography>.<prefix>.<model>`: D16), several allowed per lab. Ordered
 * with `INSTANCE_DEFAULT_LABS` first, then by ID.
 */
export const MODEL_LABS = GENERATED_LABS;
export type LabId = keyof typeof GENERATED_LABS;
export const LAB_IDS = Object.keys(MODEL_LABS) as LabId[];

export function isLabId(value: string): value is LabId {
  return Object.hasOwn(MODEL_LABS, value);
}

/** Each lab's Bedrock model ID prefixes (D16): generated, so another lab is a sync, not code. */
export const BEDROCK_LAB_PREFIXES: Readonly<Record<LabId, readonly string[]>> = Object.fromEntries(
  LAB_IDS.map((lab) => [lab, MODEL_LABS[lab].bedrockPrefixes]),
) as unknown as Record<LabId, readonly string[]>;

/** Every (lab, prefix) pair of `labs` (the stage's grant iterates these, D16). */
export function labPrefixPairs(labs: readonly string[]): { lab: LabId; prefix: string }[] {
  return labs
    .filter(isLabId)
    .flatMap((lab) => BEDROCK_LAB_PREFIXES[lab].map((prefix) => ({ lab, prefix })));
}

/** The lab whose Bedrock prefix `prefix` is, if any. */
export function labOfPrefix(prefix: string): LabId | undefined {
  return LAB_IDS.find((lab) => BEDROCK_LAB_PREFIXES[lab].includes(prefix));
}

/** Every tenant's default labs (`INSTANCE_DEFAULT_LABS`), typed. */
export const DEFAULT_LABS: readonly LabId[] = INSTANCE_DEFAULT_LABS;

/**
 * Relative cost (1 cheapest to 4) and speed (1 slowest to 3) of a family's models, by tier
 * (D3). Deep is 3/2, the value the shipped Opus offerings carried, so no consent was
 * invalidated by deriving it (slice 4b implementation note). `other` ranks between Fast and
 * Deep, like Balanced.
 */
export const TIER_IMPLICATIONS: Readonly<
  Record<FamilyTier, { readonly relativeCost: number; readonly relativeSpeed: number }>
> = {
  fast: { relativeCost: 1, relativeSpeed: 3 },
  balanced: { relativeCost: 2, relativeSpeed: 2 },
  deep: { relativeCost: 3, relativeSpeed: 2 },
  frontier: { relativeCost: 4, relativeSpeed: 1 },
  other: { relativeCost: 2, relativeSpeed: 2 },
};

export type FamilyId = keyof typeof GENERATED_FAMILIES;

/**
 * Every family ESI offers: the generated families (the drift test requires one per canonical
 * family of an offering) with `FAMILY_OVERRIDES` applied (name, tier, retention, implications
 * version, adaptive reasoning), in the generated order (by lab, then derived tier).
 */
export const MODEL_FAMILIES: { readonly [K in FamilyId]: ModelFamily } = Object.fromEntries(
  (Object.keys(GENERATED_FAMILIES) as FamilyId[]).map((id) => {
    const generated = GENERATED_FAMILIES[id];
    const override = FAMILY_OVERRIDES[id] ?? {};
    return [
      id,
      {
        id,
        lab: generated.lab,
        displayName: override.displayName ?? generated.displayName,
        tier: override.tier ?? generated.tier,
        retention: override.retention ?? "default",
        implicationsVersion: override.implicationsVersion ?? 1,
        adaptiveReasoningFrom: override.adaptiveReasoningFrom ?? null,
      } satisfies ModelFamily,
    ];
  }),
) as { readonly [K in FamilyId]: ModelFamily };
export const FAMILY_IDS = Object.keys(MODEL_FAMILIES) as FamilyId[];

export function isFamilyId(value: string): value is FamilyId {
  return Object.hasOwn(MODEL_FAMILIES, value);
}

/** The family of a lab at a tier (the first listed); undefined when the lab has none. */
export function tierFamily(lab: string, tier: FamilyTier): FamilyId | undefined {
  return FAMILY_IDS.find(
    (id) => MODEL_FAMILIES[id].lab === lab && MODEL_FAMILIES[id].tier === tier,
  );
}

/** The labs that have a family (every served lab), in catalogue order (default labs first). */
export function catalogueLabs(): LabId[] {
  return LAB_IDS.filter((lab) => FAMILY_IDS.some((id) => MODEL_FAMILIES[id].lab === lab));
}

/** The effort an answer role runs at, and the catalogue tier it maps to by default (D4). */
export const ROLE_EFFORT_TIER = {
  fast: "fast",
  chat: "balanced",
  reasoning: "deep",
} as const satisfies Record<Exclude<ModelRole, "vision">, EffortTier>;

/** An effort's family per scope (`models.effortFamilies`): absent efforts use the default. */
export interface EffortFamilies {
  readonly fast?: string | undefined;
  readonly balanced?: string | undefined;
  readonly deep?: string | undefined;
}

/** What candidate derivation reads from a scope (D4). */
export interface DerivationScope {
  /** `models.labs`: the labs offered, the first being the primary lab. */
  readonly labs: readonly string[];
  /** `models.effortFamilies`. */
  readonly effortFamilies: EffortFamilies;
  /** Whether a catalogued model accepts image input (the `vision` filter). */
  readonly acceptsImages: (key: OfferingKey) => boolean;
  /**
   * Whether a catalogued model may serve a role (the capability gate, D4: tool use, context,
   * streaming, image input). Absent: the catalogue's own fields (`meetsRoleNeeds`).
   */
  readonly capable?: (key: OfferingKey, role: ModelRole) => boolean;
}

/** Neighbours by tier (D4): Fast → Balanced, Deep → Balanced, Balanced → Fast. */
const NEIGHBOUR_TIER: Readonly<Record<EffortTier, EffortTier>> = {
  fast: "balanced",
  balanced: "fast",
  deep: "balanced",
};

/**
 * A lab's family for an effort tier (D3): its family of that tier; a lab without one (a single
 * or two-family lab) maps Fast and Deep to its `balanced` family (Deep to the dearer of two).
 */
export function labTierFamily(lab: string, tier: EffortTier): FamilyId | undefined {
  return tierFamily(lab, tier) ?? (tier === "balanced" ? undefined : tierFamily(lab, "balanced"));
}

/**
 * The family an effort resolves to in a scope: the scope's map when it names a family of an
 * offered lab, else the primary lab's family of the effort's tier.
 */
export function effortFamily(
  tier: EffortTier,
  scope: Pick<DerivationScope, "labs" | "effortFamilies">,
): FamilyId | undefined {
  const mapped = scope.effortFamilies[tier];
  if (
    mapped !== undefined &&
    isFamilyId(mapped) &&
    scope.labs.includes(MODEL_FAMILIES[mapped].lab)
  ) {
    return mapped;
  }
  const [primary] = scope.labs;
  return primary === undefined ? undefined : labTierFamily(primary, tier);
}

/**
 * The neighbour family (D4), always of the same lab: a tier family's neighbour tier; a frontier
 * or `other` family mapped to an effort falls back to that effort's own tier family; a
 * single-family lab has none.
 */
export function neighbourFamily(family: FamilyId, effortTier: EffortTier): FamilyId | undefined {
  const { lab, tier } = MODEL_FAMILIES[family];
  const target = tier === "frontier" || tier === "other" ? effortTier : NEIGHBOUR_TIER[tier];
  const neighbour = labTierFamily(lab, target);
  return neighbour === family ? undefined : neighbour;
}
